import { stringToNumber } from 'util/stringToNumber';
import styled from 'styled-components';
import { Icon, Divider } from '@web-chat-core/ui-kit';
import { IconedTextButton } from 'ui/components/Button'; // Button

// Button
export const ParticipantButton = styled(IconedTextButton)<{
    active: boolean;
    isHideTooltip?: boolean;
}>`
    position: relative;
    background-color: ${(props) =>
        props.active ? props.theme.root.colors.basicColors.white['80%'] : props.theme.root.colors.basicColors['10%']};
    color: ${(props) =>
        props.active ? props.theme.root.colors.basicColors.black['95%'] : props.theme.root.colors.basicColors['90%']};
    padding-left: 12px;
    padding-right: 16px;
    min-height: 40px;

    &:hover {
        background-color: ${(props) =>
            props.active
                ? props.theme.root.colors.basicColors.white['100%']
                : props.theme.root.colors.basicColors['15%']} !important;
    }

    ::after {
        right: 0;
        left: auto;
        transform: translateY(-100%);
    }

    @media (max-width: 991px) {
        border-radius: 6px;
        min-height: 32px;
    }
`;

export const ParticipantCounter = styled.div`
    font-size: 14px;
    font-weight: 500;
`;

// List
export const List = styled.ul`
    display: flex;
    flex-direction: column;
    list-style: none;
    padding: 0 12px 8px;
    margin: 0;
    gap: 4px;
`;

// Item
export const ListItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 40px;
    width: 100%;
    overflow: hidden;
`;

export const ListItemNameWrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    flex: 1;
`;

export const ListItemGuestLabel = styled.span`
    color: ${(props) => props.theme.root.colors.basicColors['50%']};
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const ListItemText = styled.span`
    color: ${(props) => props.theme.root.colors.basicColors['90%']};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    :only-child {
        padding-right: 48px;
    }
`;

export const ListItemUserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow: hidden;
`;

export const ListItemAvatar = styled.div<{ sid: string }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.root.colors.basicColors.white['100%']};
    background: ${(props) => props.theme.getAvatarColorsById(stringToNumber(props.sid)).background};
    overflow: hidden;
`;

export const ListItemAvatarImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
    -webkit-user-drag: none;
`;

export const ListItemIconAnimated = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        animation: pulse 1s infinite ease-in-out alternate;
    }

    @keyframes pulse {
        from {
            transform: scale(1);
        }
        to {
            transform: scale(0.95);
        }
    }
`;

export const ListIconsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ListIconWrapper = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    flex-shrink: 0;
`;

export const AddParticipantsIcon = styled(Icon)<{ $active: boolean }>`
    --icon-color: ${({ $active, theme }) =>
        $active ? theme.root.colors.basicColors.black['95%'] : theme.root.colors.basicColors['90%']};
`;

export const WaitingAddParticipantsIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => props.theme.root.colors.basicColors['15%']};
    min-width: 36px;
    height: 36px;
    border-radius: 50%;
`;

export const WaitingAddParticipantsIcon = styled(Icon)<{ $active: boolean }>`
    --icon-color: ${({ $active, theme }) =>
        $active ? theme.root.colors.basicColors.black['95%'] : theme.root.colors.basicColors['90%']};
`;
export const WaitingRequestsDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.theme.root.colors.accent.textOrIcon};
    position: absolute;
    top: 8px;
    left: 26px;
`;

export const WaitingSection = styled.li`
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    padding: 8px 0;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.04);
`;

export const SectionHeader = styled.button`
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 40px;
    padding: 0 4px;
    color: ${(props) => props.theme.root.colors.basicColors['80%']};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
`;

export const WaitingSectionHeader = styled(SectionHeader)`
    padding: 0 12px;
`;

export const ParticipantsDivider = styled(Divider)`
    width: auto;
    margin: 0 12px;
`;

export const WaitingSectionTitle = styled.span`
    font-size: 14px;
    line-height: 18px;
    font-weight: 700;
    color: ${(props) => props.theme.root.colors.basicColors['55%']};
    letter-spacing: 0.25px;
`;

export const WaitingSectionAction = styled.button`
    margin-top: 8px;
    margin-bottom: 12px;
    border: none;
    background: transparent;
    margin-left: 6px;
    padding: 6px;
    border-radius: 12px;
    cursor: pointer;
    align-self: flex-start;
    color: ${(props) => props.theme.root.button.primary.background};
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    text-align: left;
    letter-spacing: 0.1px;

    &:hover:not(:disabled) {
        background-color: ${(props) => props.theme.root.colors.basicColors['6%']};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

export const WaitingUsersList = styled.ul`
    list-style: none;
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 2px 12px 0;
    gap: 8px;
`;

export const WaitingUserItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 40px;
    box-sizing: border-box;
`;

export const WaitingUserMain = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
`;

export const WaitingUserName = styled.div`
    color: ${(props) => props.theme.root.colors.basicColors['90%']};
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
    letter-spacing: 0.1px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const WaitingUserSub = styled.div`
    color: ${(props) => props.theme.root.colors.basicColors['50%']};
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.14px;
`;

export const WaitingUserActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

export const WaitingUserActionButton = styled.button<{ $kind: 'approve' | 'reject' }>`
    border: none;
    background: transparent;
    cursor: pointer;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${(props) =>
        props.$kind === 'approve'
            ? props.theme.root.colors.accent.textOrIcon
            : props.theme.root.colors.basicColors['55%']};

    &:hover:not(:disabled) {
        background: ${(props) => props.theme.root.colors.basicColors['8%']};
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

export const ParticipantsSection = styled.li`
    display: flex;
    flex-direction: column;
`;

export const ParticipantsSectionHeader = styled(SectionHeader)`
    font-size: 14px;
    line-height: 18px;
    font-weight: 700;
    color: ${(props) => props.theme.root.colors.basicColors['55%']};
    letter-spacing: 0.25px;
`;

export const ParticipantsListInner = styled.ul`
    list-style: none;
    margin: 0 4px 0 0;
    padding-left: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const ParticipantsSectionDivider = styled(Divider)`
    margin: 0 0 6px 0;
`;

export const ParticipantModalFooterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
`;
